import type {
  DomainPropertySummary,
  DomainPropertyIR,
  ManifestFilterByInput,
} from "@granular-software/metamodel-core";
import { defineMetamodelPackage } from "@granular-software/metamodel-core";

export function defaultFilterOperators(scalarType?: string): string[] {
  switch ((scalarType || "").toLowerCase()) {
    case "number":
    case "date":
      return [
        "equal_to",
        "greater_than",
        "greater_than_or_equal_to",
        "less_than",
        "less_than_or_equal_to",
        "not_null",
      ];
    case "boolean":
      return ["equal_to", "true", "false", "not_null"];
    default:
      return [
        "equal_to",
        "in",
        "contains",
        "not_contains",
        "starts_with",
        "ends_with",
        "not_null",
      ];
  }
}

function supportsDefaultFilterBy(scalarType?: string): boolean {
  return ["string", "number", "boolean", "date"].includes(
    String(scalarType || "").toLowerCase(),
  );
}

function normalizeFilterByInput(
  filterBy?: boolean | string[] | ManifestFilterByInput,
  scalarType?: string,
) {
  if (filterBy === false) return null;
  if (filterBy === undefined) {
    if (!supportsDefaultFilterBy(scalarType)) return null;
    return { operators: defaultFilterOperators(scalarType), scalarType };
  }
  if (filterBy === true) {
    return { operators: defaultFilterOperators(scalarType), scalarType };
  }
  if (Array.isArray(filterBy)) {
    return { operators: filterBy, scalarType };
  }
  return {
    operators: filterBy.operators,
    scalarType: filterBy.scalarType || scalarType,
  };
}

export function buildFilterByFieldMutations(
  fieldPath: string,
  filterBy?: boolean | string[] | ManifestFilterByInput,
  scalarType?: string,
) {
  const normalized = normalizeFilterByInput(filterBy, scalarType);
  if (!normalized || normalized.operators.length === 0) return [];

  return [
    {
      label: `set filterBy on ${fieldPath}`,
      query: `mutation { at(path: ${JSON.stringify(fieldPath)}) { set_filter_by(operators: ${JSON.stringify(
        normalized.operators,
      )}${normalized.scalarType ? `, scalar_type: ${JSON.stringify(normalized.scalarType)}` : ""}) { operators } } }`,
    },
  ];
}

export const filterByMetamodelPackage = defineMetamodelPackage({
  id: "filter_by",
  docs: {
    fieldRows: [
      {
        key: "filterBy",
        description:
          "Exposes filter operators for generated query surfaces. Scalar fields are filterable by default; set `filterBy: false` to opt out, or override with `true`, an operator array, or `{ operators, scalarType }`.",
      },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type FilterByMetamodel {
        model: Model!
        scalar_type: String
        operators: [String!]!
      }

      extend type Model {
        filter_by: FilterByMetamodel
      }

      extend type ModelMutation {
        set_filter_by(operators: [String!]!, scalar_type: String): FilterByMetamodel
      }
      `,
    ],
    createResolvers({ run }) {
      return {
        FilterByMetamodel: {
          model: (value) => value.model,
          scalar_type: (value) => value.scalar_type || null,
          operators: (value) => value.operators,
        },
        Model: {
          filter_by: async (ant) => await run(ant.filter_by()),
        },
        ModelMutation: {
          set_filter_by: async (ant, { operators, scalar_type }) => {
            const model = await run(ant.set_filter_by(operators, scalar_type));
            return { model, operators, scalar_type };
          },
        },
      };
    },
  },
  manifest: {
    buildFieldMutations(fieldPath, spec) {
      return buildFilterByFieldMutations(fieldPath, spec.filterBy, spec.type);
    },
  },
  summary: {
    selections: {
      propertyFields: [`filter_by { scalar_type operators }`],
    },
    readPropertySummary(rawProperty) {
      if (
        rawProperty.filterBy === false ||
        rawProperty.filter_by_disabled === true
      ) {
        return { filterBy: false };
      }
      const operators = Array.isArray(rawProperty.filter_by?.operators)
        ? rawProperty.filter_by.operators.filter(
            (value: unknown): value is string =>
              typeof value === "string" && value.length > 0,
          )
        : [];
      if (operators.length === 0) return { filterBy: null };
      const scalarType =
        typeof rawProperty.filter_by?.scalar_type === "string"
          ? rawProperty.filter_by.scalar_type
          : null;
      return {
        filterBy: scalarType ? { operators, scalarType } : { operators },
      };
    },
  },
  domain: {
    applyToPropertyIR(
      propertyIR: DomainPropertyIR,
      propertySummary: DomainPropertySummary,
    ) {
      const operators =
        propertySummary.filterBy && typeof propertySummary.filterBy === "object"
          ? propertySummary.filterBy.operators || []
          : [];
      if (operators.length === 0) return propertyIR;

      return {
        ...propertyIR,
        docs: [
          ...propertyIR.docs,
          `Filter operators: ${operators.join(", ")}.`,
        ],
      };
    },
  },
});
